// todo https://boost.allnetru.dev/api/documentation/partner#/Landings
import { createApi } from '@reduxjs/toolkit/query/react';
import { GetStatisticsMetricsResponse, GetStatisticsParams, GetStatisticsResponse } from './models';

import { useApiReq } from 'lib.hooks/src';
import { apiBaseUrl } from 'lib.shared/src/api';
import { baseQueryWithReauth } from '../main/baseQueryWithReauth';
import { OrganizationRole } from '../main/organization/organizationType';
const analyticsApi = `partner_analytics`;

export const buildAnalyticsQueryString = ({
  events,
  landings,
  campaigns,
  campaignStatuses,
  partners,
  dateFrom,
  dateTo,
  gap,
  breakdown,
}: Partial<GetStatisticsParams>): string => {
  const params = new URLSearchParams();

  const appendArray = (values: string[] | undefined, key: string) => {
    values?.forEach((value) => {
      if (value !== undefined && value !== null && `${value}`.length > 0) {
        params.append(`filters[${key}][]`, value.toString());
      }
    });
  };

  appendArray(events, 'events');
  appendArray(landings, 'landings');
  appendArray(campaigns, 'campaigns');
  appendArray(campaignStatuses, 'campaign_status');
  appendArray(partners, 'partners');

  if (dateFrom) {
    params.append('filters[date][from]', dateFrom);
  }

  if (dateTo) {
    params.append('filters[date][to]', dateTo);
  }

  if (gap) {
    params.append('filters[gap]', gap);
  }

  if (breakdown) {
    params.append('filters[breakdown]', breakdown);
  }

  return params.toString();
};

export const analytics = createApi({
  reducerPath: analyticsApi,
  baseQuery: baseQueryWithReauth,
  tagTypes: [analyticsApi],
  endpoints: (build) => ({
    getAnalyticsMetrics: build.query<GetStatisticsMetricsResponse, OrganizationRole>({
      query: (role) => {
        return {
          url: `/${role || 'partner'}/statistics/metrics`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
      providesTags: [analyticsApi],
    }),

    getAnalytics: build.query<GetStatisticsResponse, GetStatisticsParams>({
      query: ({
        events,
        landings,
        campaigns,
        campaignStatuses,
        partners,
        dateFrom,
        dateTo,
        gap,
        breakdown,
        role,
      }) => {
        const queryString = buildAnalyticsQueryString({
          campaigns,
          campaignStatuses,
          landings,
          events,
          partners,
          dateFrom,
          dateTo,
          gap,
          breakdown,
        });

        return {
          url: `/${role || 'partner'}/statistics${queryString.length ? '?' + queryString : ''}`,
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
        };
      },
      providesTags: [analyticsApi],
      keepUnusedDataFor: 0,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      refetchOnMountOrArgChange: true,
    }),
  }),
});

export const { useGetAnalyticsMetricsQuery, useGetAnalyticsQuery } = analytics;

export const useGetAnalyticsExported = (role: OrganizationRole) => {
  return useApiReq<Blob, GetStatisticsParams>({
    reqConfig: {
      method: 'GET',
      url: `${apiBaseUrl}/${role}/statistics/export`,
      responseType: 'blob',
      headers: {
        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
      paramsSerializer: {
        serialize: (params) => buildAnalyticsQueryString(params as GetStatisticsParams),
      },
    },
  });
};
